import { useEffect, useRef } from 'react';

interface Point {
  x: number;
  y: number;
}

interface Circle {
  pos: Point;
  rad: number;
  color: string;
}

interface Obj {
  struct: Circle;
  drawFunc: (circle: Circle) => void;
}

interface Layer {
  zindex: number;
  objs: Obj[];
}

interface Parallax {
  regPoint: Point;
  layers: Layer[];
}

export const useTigerLilyAnimation = (containerRef: React.RefObject<HTMLDivElement>) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const layersRef = useRef<Layer[]>([]);
  const paramRef = useRef<number>(0);
  const parallaxRef = useRef<Parallax | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    
    if (!container || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize dimensions
    let width = container.offsetWidth || 1200;
    let height = container.offsetHeight || 600;
    
    const updateCanvasSize = () => {
      // Get container dimensions
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      
      // Use container dimensions, but cap height to prevent excessive vertical stretching
      // Limit to viewport height or a reasonable max (1200px)
      width = containerWidth || 1200;
      const maxHeight = Math.min(window.innerHeight || 1200, 1200);
      height = Math.min(containerHeight || 600, maxHeight);
      
      // Set internal canvas resolution to match CSS size exactly
      // This prevents stretching by ensuring 1:1 pixel mapping
      canvas.width = width;
      canvas.height = height;
      
      // CSS size matches internal resolution to avoid browser scaling/stretching
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.translate(canvas.width / 2, canvas.height / 2);
    };

    updateCanvasSize();

    // Tiger Lily color palette - Brown-blue tones (rust brown + slate blue)
    const colors = [
      '139, 69, 19',   // Rust brown
      '160, 82, 45',   // Sienna brown
      '112, 128, 144', // Slate blue
      '95, 110, 130',  // Slate blue-grey
      '120, 80, 50',   // Brown-rust mix
      '85, 100, 120',  // Slate blue-dark
      '139, 100, 70'   // Brown-blue mix
    ];

    // Helper functions
    const rand = (min: number, max: number): number => {
      const dist = max - min;
      return Math.floor(Math.random() * dist) + min;
    };

    const createPoint = (x: number, y: number): Point => ({ x, y });

    const createCircle = (pos: Point, rad: number, color: string): Circle => ({
      pos,
      rad,
      color
    });

    const drawCircle = (circle: Circle) => {
      ctx.beginPath();
      ctx.fillStyle = circle.color || '#19284B';
      ctx.arc(circle.pos.x, circle.pos.y, circle.rad, 0, 2 * Math.PI, false);
      ctx.fill();
      ctx.closePath();
    };

    const createObj = (struct: Circle, drawFunc: (circle: Circle) => void): Obj => ({
      struct,
      drawFunc
    });

    const createLayer = (zindex: number, objs: Obj[]): Layer => ({
      zindex,
      objs
    });

    const createParallax = (regPoint: Point, layers: Layer[]): Parallax => ({
      regPoint,
      layers
    });

    const copy = (src: any): any => {
      const str = JSON.stringify(src);
      return JSON.parse(str);
    };

    const translate = (pointA: Point, pointB: Point): Point => {
      return createPoint(pointA.x + pointB.x, pointA.y + pointB.y);
    };

    // Create layers with EXACT settings from debug file
    const layers: Layer[] = [];
    for (let i = 1; i <= 7; i++) {
      const objs: Obj[] = [];
      // Increase dot count to ensure full canvas coverage
      // Base count per layer, ensuring enough dots for larger canvases
      // Scale with canvas area to ensure even distribution
      const canvasArea = width * height;
      const baseCount = Math.max(200, Math.floor(canvasArea / 5000)); // More dots for better coverage
      const speckleCount = Math.floor(baseCount / i);
      
      for (let j = 0; j < speckleCount; j++) {
        // Canvas is centered at (0,0) after translate, so visible area is:
        // Horizontal: -width/2 to width/2
        // Vertical: -height/2 to height/2
        // Generate dots evenly across the FULL visible area plus margin for movement
        const halfWidth = Math.floor(width / 2);
        const halfHeight = Math.floor(height / 2);
        const x = rand(-halfWidth - 100, halfWidth + 100); // Full width + larger margin
        const y = rand(-halfHeight - 100, halfHeight + 100); // Full height + larger margin to ensure bottom coverage
        const color = colors[rand(0, colors.length)];
        
        const sizeType = Math.random();
        let radius: number;
        if (sizeType < 0.5) {
          radius = rand(1, 3);
        } else if (sizeType < 0.8) {
          radius = rand(3, 6);
        } else {
          radius = rand(6, 10);
        }
        
        const opacity = (0.4 + Math.random() * 0.3) * (i / 7);
        
        objs.push(createObj(
          createCircle(createPoint(x, y), radius, `rgba(${color}, ${opacity})`),
          drawCircle
        ));
      }
      layers.push(createLayer(i / 7, objs));
    }

    layersRef.current = layers;

    // Initialize parallax (no mouse interaction)
    const myParallax = createParallax(createPoint(1, 1), layers);
    parallaxRef.current = myParallax;

    // Clear function
    const clear = () => {
      ctx.fillStyle = '#F5F5F0'; // Silky matte white
      ctx.fillRect(-canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
    };

    // Draw function (no parallax - all layers move at same speed)
    const drawParallax = (myParallax: Parallax) => {
      for (let i = 0; i < myParallax.layers.length; i++) {
        const thisObjs = myParallax.layers[i].objs;
        
        for (let j = 0; j < thisObjs.length; j++) {
          const objCopy = copy(thisObjs[j]);
          objCopy.struct.pos = translate(objCopy.struct.pos, myParallax.regPoint);
          // Removed parallax: no layerZindex multiplication - all objects move at same speed
          
          thisObjs[j].drawFunc(objCopy.struct);
        }
      }
    };

    // Animation loop
    const animate = () => {
      clear();
      
      const myParallax = parallaxRef.current;
      if (!myParallax) return;

      // EXACT values from debug file
      const fx = Math.sin(paramRef.current * 1.5) * 40 + 30;
      
      // Removed mouse parallax - only auto-animation
      myParallax.regPoint.x = Math.cos(paramRef.current) * fx;
      myParallax.regPoint.y = Math.sin(paramRef.current * 0.7) * fx;
      paramRef.current += 0.0012; // 2x faster than before (was 0.0006, now 0.0012)
      
      drawParallax(myParallax);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Mouse event handlers removed - no parallax functionality

    // Start animation
    animate();

    // Handle resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateCanvasSize();
        // Recreate layers with new dimensions
        const newLayers: Layer[] = [];
        for (let i = 1; i <= 7; i++) {
          const objs: Obj[] = [];
          // Increase dot count to ensure full canvas coverage
          // Scale with canvas area to ensure even distribution
          const canvasArea = width * height;
          const baseCount = Math.max(200, Math.floor(canvasArea / 5000)); // More dots for better coverage
          const speckleCount = Math.floor(baseCount / i);
          
          for (let j = 0; j < speckleCount; j++) {
            // Canvas is centered at (0,0) after translate, so visible area is:
            // Horizontal: -width/2 to width/2
            // Vertical: -height/2 to height/2
            // Generate dots evenly across the FULL visible area plus margin for movement
            const halfWidth = Math.floor(width / 2);
            const halfHeight = Math.floor(height / 2);
            const x = rand(-halfWidth - 100, halfWidth + 100); // Full width + larger margin
            const y = rand(-halfHeight - 100, halfHeight + 100); // Full height + larger margin to ensure bottom coverage
            const color = colors[rand(0, colors.length)];
            
            const sizeType = Math.random();
            let radius: number;
            if (sizeType < 0.5) {
              radius = rand(1, 3);
            } else if (sizeType < 0.8) {
              radius = rand(3, 6);
            } else {
              radius = rand(6, 10);
            }
            
            const opacity = (0.4 + Math.random() * 0.3) * (i / 7);
            
            objs.push(createObj(
              createCircle(createPoint(x, y), radius, `rgba(${color}, ${opacity})`),
              drawCircle
            ));
          }
          newLayers.push(createLayer(i / 7, objs));
        }
        layersRef.current = newLayers;
        if (parallaxRef.current) {
          parallaxRef.current.layers = newLayers;
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  return canvasRef;
};

